import { TURNSTILE_SITE_KEY } from '../config/env';

/**
 * Cloudflare Turnstile token provider for the heavy-media captcha gate.
 *
 * The backend only challenges a request when TURNSTILE_SECRET is configured;
 * this client only acquires a token when TURNSTILE_SITE_KEY is set. With no
 * site key (dev / pre-rollout) getTurnstileToken() returns null and callers
 * send no header — so the feature is fully env-gated on both sides.
 */

let widgetId = null;
let loadingWidget = null;
let widgetPromise = null;
// Serializes reset()+execute() so concurrent callers never interleave on the
// single widget (Turnstile runs one challenge at a time).
let tokenChain = Promise.resolve();
// Whether the widget has run at least one challenge. Calling reset() on a
// freshly rendered execute-mode widget that has NEVER executed throws Turnstile
// error 300031 — so we only reset AFTER the first execution (to clear an
// already-consumed token before minting a fresh one).
let hasExecuted = false;

function ensureScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve();
  if (loadingWidget) return loadingWidget;
  loadingWidget = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile script load failed'));
    document.head.appendChild(s);
  });
  return loadingWidget;
}

async function ensureWidget() {
  if (widgetPromise) return widgetPromise;
  widgetPromise = (async () => {
    await ensureScript();
    const container = document.createElement('div');
    // Keep the widget off-screen but rendered (Turnstile needs it in the
    // layout to issue a token). Never display:none — that can suppress tokens.
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    document.body.appendChild(container);
    return new Promise((resolve, reject) => {
      try {
        // NOTE: Turnstile's `size` only accepts 'normal' | 'flexible' | 'compact'.
        // 'invisible' is rejected by api.js for a Managed/Non-interactive widget,
        // so we render a compact widget off-screen (effectively invisible for the
        // common managed case). For a true no-checkbox widget, set the Turnstile
        // widget type to "Invisible" in the Cloudflare dashboard for this sitekey.
        widgetId = window.turnstile.render(
          container,
          { sitekey: TURNSTILE_SITE_KEY, size: 'compact', execution: 'execute' },
          (id) => resolve(id),
        );
      } catch (e) {
        widgetPromise = null; // allow a retry on the next call
        reject(e);
      }
    });
  })();
  return widgetPromise;
}

/**
 * Fresh Turnstile token, or null when captcha is disabled / unavailable.
 *
 * NOTE: the guard must NOT check `window.turnstile` — the widget (and the
 * Turnstile script itself) is loaded lazily inside `ensureWidget()`, so on the
 * very first call `window.turnstile` is always undefined. Checking it here made
 * the function return null forever and silently disabled the whole captcha
 * rollout (no header ever sent, backend gate never exercised).
 */
export async function getTurnstileToken() {
  if (!TURNSTILE_SITE_KEY || typeof window === 'undefined') return null;
  // Turnstile tokens are SINGLE-USE at siteverify — a replayed token is rejected
  // with `timeout-or-duplicate`. So every heavy-media request must carry a FRESH
  // token. Concurrent callers are serialized so execute() never interleaves on
  // the one widget.
  const run = tokenChain.then(async () => {
    try {
      const id = await ensureWidget();
      if (!window.turnstile) return null; // script failed to load
      return await new Promise((resolve) => {
        try {
          // reset() clears a previously-consumed token so the next execute()
          // yields a FRESH one. But resetting a freshly rendered execute-mode
          // widget that has never run a challenge throws Turnstile error 300031
          // — so only reset AFTER the first execution.
          if (hasExecuted) window.turnstile.reset(id);
        } catch {
          /* reset unavailable — proceed to execute anyway */
        }
        hasExecuted = true;
        window.turnstile.execute(id, { callback: (tok) => resolve(tok || null) });
      });
    } catch {
      return null;
    }
  });
  tokenChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/** Append a fresh captcha token as a query param (for <audio>/<video> src that
 *  cannot carry request headers). Returns the URL unchanged when disabled. */
export async function appendTurnstileToken(url) {
  const tk = await getTurnstileToken();
  if (!tk) return url;
  return url + (String(url).includes('?') ? '&' : '?') + 'turnstile=' + encodeURIComponent(tk);
}
